// workspaces.ts — mapa nawigacji Control Center.
//
// Model (R5-15 §18): górny pasek przełącza PRZESTRZENIE ROBOCZE, lewy pasek pokazuje funkcje
// wewnątrz jednej przestrzeni. Ani „każdy ekran to osobna aplikacja”, ani „jeden wielki komponent”.
//
// Uprawnienia przy pozycjach służą WYŁĄCZNIE do chowania nawigacji (UX). Autoryzacja jest
// po stronie backendu — patrz nagłówek permissions.ts.

import type { Permission } from '@/lib/auth/permissions';

/** Dojrzałość ekranu — pokazywana użytkownikowi, żeby nie brał szkicu za gotowe narzędzie. */
export type SectionMaturity =
  | 'live' // działa na realnym API
  | 'local' // narzędzie działa (podgląd, walidacja, szkic), ale niczego nie publikuje — opis w `note`
  | 'planned' // celowo pusty, zaprojektowany, jeszcze nie zbudowany
  | 'blocked'; // zbudowanie zablokowane przez udowodniony blocker (opis w `note`)

/** Etykieta plakietki przy sekcji, która nie jest `live`. */
export function etykietaDojrzalosci(m: Exclude<SectionMaturity, 'live'>): string {
  switch (m) {
    case 'local':
      return 'bez publikacji';
    case 'blocked':
      return 'zablokowane';
    case 'planned':
      return 'zaplanowane';
  }
}

export interface WorkspaceSection {
  id: string;
  /** Ścieżka względem przestrzeni roboczej. Pusty string = ekran domyślny. */
  path: string;
  label: string;
  maturity: SectionMaturity;
  permissions?: readonly Permission[];
  note?: string;
}

export interface Workspace {
  id: string;
  path: string;
  label: string;
  sections: readonly WorkspaceSection[];
}

// ŚWIADOMA DECYZJA: górny pasek pokazuje CAŁĄ mapę produktu, także przestrzenie, w których
// dana osoba nie zobaczy jeszcze danych. Ekrany bez danych (zaplanowane/zablokowane) nie mają
// uprawnień, bo nie mają czego chronić — mówią wyłącznie, czego jeszcze nie ma i dlaczego.
// Uprawnienia siedzą tam, gdzie są DANE. Przestrzeń znika z paska dopiero wtedy, gdy żadna
// jej sekcja nie jest dla tej osoby widoczna.

export const WORKSPACES: readonly Workspace[] = [
  {
    id: 'home',
    path: '/',
    label: 'Start',
    sections: [{ id: 'overview', path: '', label: 'Przegląd', maturity: 'live' }],
  },
  {
    /*
     * ADMINISTRACJA (OWNER 2026-10-10): pięć narzędzi administratora w jednym miejscu, w tej kolejności.
     * Rozliczenia i Grafik PRZENIESIONE tu z Restauracji — te same komponenty i te same trasy API,
     * zmienia się wyłącznie adres w nawigacji. Stare adresy (`#/admin/…`, `#/restauracje/rozliczenia`,
     * `#/restauracje/grafik`) przekierowuje router z zachowaniem parametrów (patrz `ALIASY` w router.tsx).
     * Dawne „Administracja → Grafik” (widok jednej restauracji) i „Organizacja” (płaska lista par)
     * usunięte: relację restauracja ↔ szkoła prowadzi „Szkoły i restauracje”, dni i godziny — Grafik.
     */
    id: 'admin',
    path: '/administracja',
    label: 'Administracja',
    sections: [
      {
        id: 'users',
        path: '',
        label: 'Użytkownicy',
        maturity: 'live',
        permissions: ['users.read'],
        note: 'Konta klientów jak w starym panelu admina (Konta): wyszukiwanie, filtry, saldo, doładowanie i usunięcie konta — przez te same operacje systemu głównego.',
      },
      {
        id: 'schools',
        path: 'szkoly-i-restauracje',
        label: 'Szkoły i restauracje',
        maturity: 'live',
        permissions: ['restaurantSchools.read'],
        note: 'Które szkoły obsługuje każda restauracja: miasto → restauracja → szkoły, z dodawaniem i odpinaniem szkół w systemie głównym. Dni i godziny dostaw ustala Grafik.',
      },
      {
        id: 'menu-moderacja',
        path: 'moderacja-menu',
        label: 'Moderacja menu',
        maturity: 'live',
        permissions: ['menuModeration.read'],
        note: 'Kolejka dań czekających na decyzję, jak w starym panelu admina (Menu): zatwierdzenie i odrzucenie przez te same operacje systemu głównego.',
      },
      {
        // Dane finansowe całej sieci — osobne uprawnienie, nie `analytics.sales.read`.
        id: 'settlements',
        path: 'rozliczenia',
        label: 'Rozliczenia',
        maturity: 'live',
        permissions: ['settlements.read'],
        note: 'Rozliczenia restauracji: tygodniowe (wypłaty), miesięczne (faktury TuSzamy) i roczne. Tylko odczyt.',
      },
      {
        // Kanoniczny grafik: odczyt i zapis przez API systemu głównego (to samo co stary panel).
        id: 'schedule',
        path: 'grafik',
        label: 'Grafik',
        maturity: 'live',
        permissions: ['schedule.read'],
        note: 'Które restauracje są dostępne w których szkołach w które dni — miasto po mieście, z liczbą restauracji i ostrzeżeniami o rozkładzie. Edycja wraca do systemu głównego.',
      },
    ],
  },
  {
    id: 'restaurants',
    path: '/restauracje',
    label: 'Restauracje',
    sections: [
      {
        id: 'sales',
        path: '',
        label: 'Sprzedaż',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'dishes',
        path: 'dania',
        label: 'Dania',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'schools',
        path: 'szkoly',
        label: 'Szkoły',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'catalog',
        path: 'katalog',
        label: 'Katalog dań',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'menu',
        path: 'menu',
        label: 'Menu',
        maturity: 'planned',
      },
    ],
  },
  {
    /*
     * OSOBNY OBSZAR, NIE SEKCJA W „RESTAURACJE".
     *
     * „Restauracje" to ANALITYKA — ile która sprzedała, co się sprzedaje, jaki katalog.
     * „Panel restauracji" to WIDOK OPERACYJNY jednej restauracji: co dziś ugotować i dokąd
     * to jedzie. Zlanie tych dwóch powtórzyłoby pomyłkę, którą właśnie rozplątaliśmy przy
     * Szkołach i Grafiku.
     */
    id: 'restaurant-panel',
    path: '/panel-restauracji',
    label: 'Panel restauracji',
    sections: [
      {
        id: 'dzisiaj',
        path: '',
        label: 'Dzisiejsze zamówienia',
        maturity: 'live',
        permissions: ['restaurant.sales.read'],
        note: 'Co i ile ugotować, i do której szkoły to jedzie. Ekran roboczy kuchni.',
      },
      {
        id: 'dania',
        path: 'dania',
        // „Dania" istnieje już pod Restauracjami jako ANALITYKA sprzedaży dań. Dwa te same
        // napisy w nawigacji mylą i operatora, i paletę poleceń. To jest KARTA dań.
        label: 'Karta dań',
        maturity: 'live',
        // Karta dań to zdolność restauracyjna, nie analityczna — stąd `restaurant.menu.read`,
        // a nie `analytics.sales.read` jak w analitycznym ekranie „Dania" pod Restauracjami.
        permissions: ['restaurant.menu.read'],
        note: 'Karta dań restauracji. Każda zmiana przechodzi przez moderację.',
      },
      {
        id: 'szkoly',
        path: 'szkoly',
        label: 'Szkoły',
        maturity: 'live',
        permissions: ['restaurant.sales.read'],
        note: 'Tylko do odczytu. Przypisania zmienia się w Administracja → Szkoły i restauracje, dni dostaw w Administracja → Grafik.',
      },
      {
        id: 'raporty-dostaw',
        path: 'raporty-dostaw',
        label: 'Raporty dostaw',
        maturity: 'live',
        // Dane systemu kierowców, ale pytanie jest restauracyjne („czy MOJE dowozy są na czas"),
        // stąd uprawnienie dostaw, a nie sprzedaży.
        permissions: ['analytics.delivery.read'],
        note: 'Punktualność, kierowcy i trafność zgłoszeń spóźnień — dla tej restauracji.',
      },
      {
        id: 'transakcje',
        path: 'transakcje',
        // „Transakcje" nosi już parytetowy ekran CRM. Dwa te same napisy w szynie i w palecie
        // poleceń zmuszają operatora do zgadywania, w który wchodzi — trzecie takie starcie
        // po „Menu" i „Daniach". Ten ekran jest HISTORIĄ, więc nazwa mówi to wprost.
        label: 'Historia transakcji',
        maturity: 'live',
        permissions: ['restaurant.sales.read'],
        note: 'Historia sprzedanych pozycji tej restauracji. Wartość katalogowa, nie rozliczenie.',
      },
    ],
  },
  {
    id: 'crm',
    path: '/crm',
    label: 'CRM',
    sections: [
      {
        id: 'school-sales',
        path: '',
        label: 'Sprzedaż wg szkół',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'transactions',
        path: 'transakcje',
        label: 'Transakcje',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'opinie',
        path: 'opinie',
        label: 'Opinie',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
        note: 'Oceny uczniów wystawione daniom. Siedem kolumn, wszystkie filtrowalne, żadna sortowalna — dokładnie jak w starym panelu.',
      },
      {
        id: 'campaigns',
        path: 'kampanie',
        label: 'Kampanie',
        maturity: 'planned',
        permissions: ['crm.campaigns.read'],
      },
    ],
  },
  {
    id: 'drive',
    path: '/drive',
    label: 'Dostawy',
    sections: [
      { id: 'deliveries', path: '', label: 'Punktualność', maturity: 'live' },
      { id: 'schools', path: 'szkoly', label: 'Wg szkół', maturity: 'live' },
      { id: 'history', path: 'historia', label: 'Historia', maturity: 'live' },
    ],
  },
  {
    id: 'insights',
    path: '/insights',
    label: 'Insighty',
    sections: [
      { id: 'all', path: '', label: 'Wszystkie sygnały', maturity: 'live', permissions: ['analytics.sales.read'] },
      {
        id: 'coincidence',
        path: 'powiazania',
        label: 'Powiązania',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
    ],
  },
  {
    /*
     * Treści aplikacji ucznia: skórki (silnik R5-10) i żółty pasek. Osobny kanał treści UI —
     * NIE kampanie CRM i NIE push.
     *
     * Dojrzałość `live` (OWNER 2026-10-02, decyzja R5-15 przy recenzji #833): zapis, publikacja,
     * planowanie, wyłączenie i rollback idą przez serwer CC do systemu głównego. Tam, gdzie serwer
     * CC nie ma drogi zapisu, mówi to EKRAN (powód przy wyłączonych przyciskach), nie nawigacja.
     */
    id: 'tresci',
    path: '/tresci',
    label: 'Treści aplikacji',
    sections: [
      {
        id: 'skorki',
        path: '',
        label: 'Skórki',
        maturity: 'live',
        permissions: ['app.content.read'],
        note: 'Walidacja paczki według kontraktu aplikacji, rewizje, planowanie, aktywacja, wyłączenie i rollback w systemie głównym.',
      },
      {
        id: 'pasek',
        path: 'pasek',
        label: 'Pasek główny',
        maturity: 'live',
        permissions: ['app.content.read'],
        note: 'Edycja i podgląd żółtego paska 1:1 z aplikacją, szkice, publikacja i wyłączanie pozycji w systemie głównym.',
      },
    ],
  },
  {
    id: 'reports',
    path: '/raporty',
    label: 'Raporty',
    sections: [{ id: 'reports', path: '', label: 'Eksporty', maturity: 'planned' }],
  },
  {
    id: 'system',
    path: '/system',
    label: 'System',
    sections: [
      { id: 'status', path: '', label: 'Status połączeń', maturity: 'live' },
      /*
       * Gotowość i Jakość danych świadomie stoją w przestrzeni System, a nie w „ustawieniach
       * zaawansowanych". Pytanie „czy mogę na tej liczbie oprzeć decyzję" pada równie często
       * jak „ile sprzedaliśmy" — a odpowiedź na pierwsze rozstrzyga, czy druga jest cokolwiek
       * warta. Schowanie ich głębiej znaczyłoby, że to szczegół techniczny. Nie jest.
       */
      { id: 'readiness', path: 'gotowosc', label: 'Gotowość', maturity: 'live' },
      { id: 'quality', path: 'jakosc', label: 'Jakość danych', maturity: 'live' },
      {
        id: 'audit',
        path: 'audyt',
        label: 'Dziennik zmian',
        maturity: 'blocked',
        note: 'Centralnego dziennika zmian jeszcze nie ma. Kontrakt docelowy: 04_Contracts/audit-contract-v0.',
      },
    ],
  },
];

export function findWorkspace(id: string): Workspace | undefined {
  return WORKSPACES.find((w) => w.id === id);
}

/** Sekcje widoczne dla danego zestawu uprawnień. Chowanie nawigacji, nie autoryzacja. */
export function visibleSections(
  workspace: Workspace,
  isVisible: (permissions?: readonly Permission[]) => boolean,
): WorkspaceSection[] {
  return workspace.sections.filter((s) => isVisible(s.permissions));
}

/** Pełna ścieżka routingu dla sekcji. */
export function sectionPath(workspace: Workspace, section: WorkspaceSection): string {
  if (workspace.path === '/') return section.path ? `/${section.path}` : '/';
  return section.path ? `${workspace.path}/${section.path}` : workspace.path;
}
